import { useQuery } from '@tanstack/react-query';
import { api } from '@/api/client';
import type { DashboardKpisResponse } from '@/types/dashboards';
import type { Period } from '@/lib/timePeriod';

/** Trailing-12-week per-rule fires/dormancy/suppression-share panel. */
export function useRuleHealth() {
  return useQuery<DashboardKpisResponse>({
    queryKey: ['dashboards', 'rule-health'],
    queryFn: async () => {
      const { data } = await api.get<DashboardKpisResponse>('/dashboards/rule-health');
      return data;
    },
    staleTime: 60_000,
  });
}

/** Spec §13 six-tile fleet data-trust panel (fixed "this month" window). */
export function useFleetHealth() {
  return useQuery<DashboardKpisResponse>({
    queryKey: ['dashboards', 'fleet-health'],
    queryFn: async () => {
      const { data } = await api.get<DashboardKpisResponse>('/dashboards/fleet-health');
      return data;
    },
    staleTime: 60_000,
  });
}

/** Weekend / after-hours spend share, midnight-bug-aware. Period-scoped. */
export function useWeekendHours(period: Period) {
  return useQuery<DashboardKpisResponse>({
    queryKey: ['dashboards', 'weekend-hours', period],
    queryFn: async () => {
      const { data } = await api.get<DashboardKpisResponse>('/dashboards/weekend-hours', {
        params: { period },
      });
      return data;
    },
    staleTime: 60_000,
  });
}

/** Top-20 stations by within-brand fraud-rate percentile (fixed 90d window). */
export function useStationRisk() {
  return useQuery<DashboardKpisResponse>({
    queryKey: ['dashboards', 'station-risk'],
    queryFn: async () => {
      const { data } = await api.get<DashboardKpisResponse>('/dashboards/station-risk');
      return data;
    },
    staleTime: 60_000,
  });
}
